<template>
  <OverlayFrame
    :zIndex="overlayZIndex"
    :title="title"
    :subtitle="subtitle"
    :icon="kindIcon"
    closeLabel="Close inspect"
    @close="emit('close')"
    wide
  >
    <div v-if="meta.length" class="meta">
      <div v-for="row in meta" :key="row.label" class="meta-row">
        <div class="meta-label">{{ row.label }}</div>
        <div class="meta-value">{{ row.value }}</div>
      </div>
    </div>

    <div class="body scroll">
      <UiPanel v-for="section in sections" :key="section.title">
        <UiSectionHeader>
          <template #title>{{ section.title }}</template>
        </UiSectionHeader>
        <div v-if="section.variant === 'coming-soon'" class="coming-soon">
          <UiCallout variant="info">Coming Soon</UiCallout>
          <div v-if="section.body" class="text-muted" style="margin-top: 0.6rem;">{{ section.body }}</div>
        </div>
        <div v-else>
          <div v-if="section.body" class="text-muted">{{ section.body }}</div>
          <slot v-else :name="section.slotName || ''" />
        </div>
      </UiPanel>
    </div>
  </OverlayFrame>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { ICONS } from "../../../lib/icons";
import OverlayFrame from "../../../components/overlay/OverlayFrame.vue";
import UiCallout from "../../../components/ui/UiCallout.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiSectionHeader from "../../../components/ui/UiSectionHeader.vue";

type InspectMetaRow = { label: string; value: string };
type InspectSection = {
  title: string;
  body?: string | null;
  variant?: "default" | "coming-soon";
  slotName?: string;
};

const props = defineProps<{
  overlayZIndex?: number;
  kind: "npc" | "player";
  title: string;
  subtitle?: string | null;
  meta: InspectMetaRow[];
  sections: InspectSection[];
}>();

const emit = defineEmits<{
  (e: "close"): void;
}>();

const kindIcon = computed(() => (props.kind === "npc" ? ICONS.inspectKinds.npc : ICONS.inspectKinds.player));
</script>

<style scoped>
.meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.6rem;
}

.meta-row {
  background: color-mix(in srgb, var(--bg-2) 72%, transparent);
  border: 1px solid var(--border-0);
  border-radius: var(--radius-sm);
  padding: 0.6rem 0.75rem;
  display: flex;
  justify-content: space-between;
  gap: 0.8rem;
  align-items: baseline;
}

.meta-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: color-mix(in srgb, var(--fg-1) 70%, transparent);
}

.meta-value {
  font-family: var(--font-mono);
  font-weight: 700;
}

.body {
  display: grid;
  gap: 1rem;
}

.coming-soon {
  margin-top: 0.6rem;
}

@media (max-width: 720px) {
  .meta {
    grid-template-columns: 1fr;
  }
}
</style>
